import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useOrgId } from './org';

/** One home-currency helper for the app (lib/org-locale). */
export { useHomeCurrency } from './org-locale';

/** Currencies, exchange rates and revaluations (document 06 G10, A13; API /orgs/:id/currencies). */
export interface CurrencyRow {
  code: string;
  name: string;
  exponent: number;
  latestRate: string | null;
  latestRateDate: string | null;
  inUse: boolean;
}

export interface CurrenciesOverview {
  enabled: boolean;
  homeCurrency: string;
  currencies: CurrencyRow[];
  available: Array<{ code: string; name: string }>;
  rateProvider: string | null;
}

export interface ExchangeRate {
  id: string;
  currency: string;
  date: string;
  rate: string;
  source: 'manual' | 'import' | 'provider';
  provider: string | null;
  updatedAt: string;
}

export interface RateOn {
  currency: string;
  date: string;
  rateDate: string | null;
  rate: string | null;
  source: ExchangeRate['source'] | 'home' | null;
}

export interface ImportRatesResult {
  rows: number;
  added: number;
  updated: number;
  unchanged: number;
  errors: Array<{ line: number; message: string }>;
  saved: boolean;
}

export interface RevaluationLine {
  accountId: string;
  accountName: string;
  contactId: string | null;
  contactName: string | null;
  foreignBalance: string;
  bookedBalance: string;
  revaluedBalance: string;
  adjustment: string;
}

export interface RevaluationPreview {
  currency: string;
  date: string;
  rate: string;
  lines: RevaluationLine[];
  gain: string;
}

export interface Revaluation {
  id: string;
  currency: string;
  date: string;
  rate: string;
  gain: string;
  status: 'posted' | 'void';
  entryId: string;
  reversalEntryId: string;
  reversalDate: string;
  voidReason: string | null;
  createdBy: string;
  createdAt: string;
}

export const SOURCE_LABEL: Record<ExchangeRate['source'], string> = {
  manual: 'Entered',
  import: 'Imported',
  provider: 'Rate provider',
};

export function useCurrencies() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['currencies', orgId],
    queryFn: () => api<CurrenciesOverview>(`/orgs/${orgId}/currencies`),
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });
}

export function useExchangeRates(currency?: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['currencies', orgId, 'rates', currency ?? 'all'],
    queryFn: () =>
      api<ExchangeRate[]>(
        `/orgs/${orgId}/exchange-rates${currency ? `?currency=${currency}` : ''}`,
      ),
    enabled: Boolean(orgId),
  });
}

/** The rate a form prefills: the latest on or before the date (null when there is none). */
export function useRateOn(currency: string | null | undefined, date: string, enabled = true) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['currencies', orgId, 'rate-on', currency, date],
    queryFn: () =>
      api<RateOn>(`/orgs/${orgId}/exchange-rates/on?currency=${currency}&date=${date}`),
    enabled: Boolean(orgId && currency && /^\d{4}-\d{2}-\d{2}$/.test(date) && enabled),
    staleTime: 60_000,
  });
}

export function useRevaluations() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['currencies', orgId, 'revaluations'],
    queryFn: () => api<Revaluation[]>(`/orgs/${orgId}/revaluations`),
    enabled: Boolean(orgId),
  });
}

// ---------------------------------------------------------------- pure helpers

/** Decimal places per currency (packages/ledger-core currency.ts). */
const EXPONENTS: Record<string, number> = { KWD: 3, BHD: 3, OMR: 3, JPY: 0 };
export const exponentOf = (code: string): number => EXPONENTS[code] ?? 2;

/** "3.672500" → "3.6725". */
export const trimRate = (r: string): string =>
  r.includes('.') ? r.replace(/0+$/, '').replace(/\.$/, '') : r;

/** A positive decimal with at most 10 places, as the API accepts. */
export const isRate = (r: string): boolean =>
  /^\d{1,10}(\.\d{1,10})?$/.test(r.trim()) && /[1-9]/.test(r);

/** "1 USD = 3.6725 AED" (QBO wording). */
export const rateLine = (currency: string, rate: string, home: string): string =>
  `1 ${currency} = ${trimRate(rate)} ${home}`;

/**
 * Minor units of `from` → minor units of home at a rate per whole unit, rounded half away from
 * zero (mirrors ledger-core convertMinorUnits; no floats).
 */
export function toHomeMinor(minor: bigint, rate: string, fromExp = 2, toExp = 2): bigint {
  const r = rate.trim();
  if (!isRate(r)) return 0n;
  const [i, f = ''] = r.split('.') as [string, string?];
  const digits = BigInt(`${i}${f ?? ''}`);
  const up = 10n ** BigInt(Math.max(0, toExp - fromExp));
  const down = 10n ** BigInt((f ?? '').length + Math.max(0, fromExp - toExp));
  const n = minor * digits * up;
  const neg = n < 0n;
  const abs = neg ? -n : n;
  const q = abs / down;
  const rounded = (abs % down) * 2n >= down ? q + 1n : q;
  return neg ? -rounded : rounded;
}

/**
 * A home-currency price ("367.25") in a foreign currency at a rate per whole unit, rounded half
 * away from zero to the currency's decimals: item prices are kept in home currency and convert
 * when an item is picked on a foreign transaction (QBO does the same).
 */
export function fromHomePrice(price: string, rate: string, toExp = 2): string {
  const p = price.trim();
  if (!/^-?\d+(\.\d+)?$/.test(p) || !isRate(rate)) return p;
  const [pi, pf = ''] = p.replace('-', '').split('.') as [string, string?];
  const [ri, rf = ''] = rate.trim().split('.') as [string, string?];
  const num = BigInt(`${pi}${pf ?? ''}`) * 10n ** BigInt((rf ?? '').length + toExp);
  const den = BigInt(`${ri}${rf ?? ''}`) * 10n ** BigInt((pf ?? '').length);
  const q = num / den;
  const minor = (num % den) * 2n >= den ? q + 1n : q;
  const s = minor.toString().padStart(toExp + 1, '0');
  const out = toExp ? `${s.slice(0, -toExp)}.${s.slice(-toExp)}` : s;
  return p.startsWith('-') && minor !== 0n ? `-${out}` : out;
}
